import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IoRefreshOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';

import { Table } from 'src/components/TableNew';
import { SearchInput } from 'src/components/SearchInput';
import { Select } from 'src/components/Select';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { useAdminPermissions } from 'src/hooks/useAdminPermissions';
import { PERMISSIONS } from 'src/utils/permissions';
import { ROUTE_LOGS } from 'src/utils/routes';
import {
  ActivityLog,
  LogSource,
  dateInputToIso,
  fetchUserActivityLogs,
  formatDurationMs,
  statusTone,
} from 'src/api/adminLogs';
import { convertDate } from 'src/components/Table/helpers';
import { useSearchQueryValue } from 'src/hooks/useDebouncedSearch';

import LogDetailModal from 'src/pages/Logs/LogDetailModal';
import {
  DateControl,
  DateInput,
  DateRangeGroup,
  DurationText,
  FieldLabel,
  FilterControl,
  SearchControl,
  SpinIcon,
  StatusCode,
  ToolButton,
  ToolbarActionButtons,
  ToolbarActions,
  ToolbarFilters,
  ToolbarRow,
} from 'src/pages/Logs/styled';
import {
  EmptyState,
  SectionControls,
  SectionTitle,
  SectionToolbar,
  TabPanel,
} from '../styled';

type ActivityTabProps = {
  userId: string;
  onForbidden?: () => void;
};

const SOURCE_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'cloud', label: 'Cloud' },
  { value: 'local', label: 'Local' },
];

const HEADERS = ['Time', 'Action', 'Status', 'Duration'];
const POLL_MS = 45_000;

const ActivityTab: React.FC<ActivityTabProps> = ({ userId, onForbidden }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { can } = useAdminPermissions();
  const canOpenFullLogs = can(PERMISSIONS.ALL);

  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useSearchQueryValue(search);
  const [selected, setSelected] = useState<ActivityLog | null>(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, status, source, fromDate, toDate, userId]);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const res = await fetchUserActivityLogs(userId, {
          page: currentPage,
          limit: 20,
          action: debouncedSearch || undefined,
          status: status || undefined,
          source: (source as LogSource) || undefined,
          from: dateInputToIso(fromDate),
          to: dateInputToIso(toDate, true),
        });
        setLogs(res.activity_logs);
        setTotalPages(Math.max(1, res.meta?.page_count || 1));
        setTotalCount(res.meta?.total_count || res.activity_logs.length);
      } catch (e: any) {
        if (e?.response?.status === 403) {
          onForbidden?.();
          return;
        }
        if (!silent) {
          dispatch(
            setAppAlert({
              message: e?.response?.data?.error ?? 'Failed to load activity',
              isSuccess: false,
            }),
          );
          setLogs([]);
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [
      userId,
      currentPage,
      debouncedSearch,
      status,
      source,
      fromDate,
      toDate,
      dispatch,
      onForbidden,
    ],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = window.setInterval(() => load(true), POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const rows = useMemo(
    () =>
      logs.map((log) => ({
        ...log,
        time_display: log.created_at
          ? `${convertDate(log.created_at).date} ${convertDate(log.created_at).time}`
          : '—',
        action_display: log.action || '—',
        status_display: String(log.status ?? '—'),
        duration_display: formatDurationMs(log.duration_ms),
      })),
    [logs],
  );

  const hasFilters = Boolean(status || source || fromDate || toDate || search);

  const resetFilters = () => {
    setStatus('');
    setSource('');
    setFromDate('');
    setToDate('');
    setSearch('');
    setCurrentPage(1);
  };

  return (
    <TabPanel>
      <SectionToolbar>
        <SectionTitle>Activity ({totalCount})</SectionTitle>
        <SectionControls>
          <ToolbarRow $flush>
            <ToolbarFilters>
              <SearchControl $width="14rem">
                <FieldLabel>Action</FieldLabel>
                <SearchInput
                  inline
                  showIcon
                  placeholder="Action prefix..."
                  value={search}
                  onChange={setSearch}
                />
              </SearchControl>
              <FilterControl $width="8rem">
                <FieldLabel>Status</FieldLabel>
                <SearchInput inline placeholder="e.g. 403" value={status} onChange={setStatus} />
              </FilterControl>
              <FilterControl>
                <FieldLabel>Source</FieldLabel>
                <Select
                  compact
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  options={SOURCE_OPTIONS}
                  placeholder="Source"
                />
              </FilterControl>
            </ToolbarFilters>
          </ToolbarRow>
          <ToolbarRow $flush>
            <DateRangeGroup>
              <DateControl>
                <FieldLabel htmlFor="user-activity-from">From</FieldLabel>
                <DateInput
                  id="user-activity-from"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </DateControl>
              <DateControl>
                <FieldLabel htmlFor="user-activity-to">To</FieldLabel>
                <DateInput
                  id="user-activity-to"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </DateControl>
            </DateRangeGroup>
            <ToolbarActions>
              <ToolbarActionButtons>
                {canOpenFullLogs && (
                  <ToolButton
                    type="button"
                    onClick={() => navigate(`${ROUTE_LOGS}?actor=${encodeURIComponent(userId)}`)}
                    title="Open full activity log for this user"
                  >
                    View activity
                  </ToolButton>
                )}
                <ToolButton
                  type="button"
                  disabled={!hasFilters || loading}
                  onClick={resetFilters}
                  title="Clear filters"
                >
                  Reset
                </ToolButton>
                <ToolButton
                  type="button"
                  $iconOnly
                  disabled={loading}
                  onClick={() => load()}
                  title="Refresh activity"
                  aria-label="Refresh activity"
                >
                  <SpinIcon $spin={loading}>
                    <IoRefreshOutline size={18} />
                  </SpinIcon>
                </ToolButton>
              </ToolbarActionButtons>
            </ToolbarActions>
          </ToolbarRow>
        </SectionControls>
      </SectionToolbar>

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={HEADERS}
          items={rows}
          columns="1.2fr 2fr 0.7fr 0.8fr"
          fieldsToShow={['time_display', 'action_display', 'status_display', 'duration_display']}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          onRowClick={(item) => setSelected(item as ActivityLog)}
          renderCell={(item, fieldName) => {
            if (fieldName === 'status_display') {
              return <StatusCode $tone={statusTone(item.status)}>{item.status ?? '—'}</StatusCode>;
            }
            if (fieldName === 'duration_display') {
              return <DurationText $tone="neutral">{item.duration_display}</DurationText>;
            }
            if (fieldName === 'action_display') {
              return <span title={item.action}>{item.action_display}</span>;
            }
            return null;
          }}
        />
      ) : (
        <EmptyState>No activity for this user.</EmptyState>
      )}

      {selected && (
        <LogDetailModal kind="activity" log={selected} onClose={() => setSelected(null)} />
      )}
    </TabPanel>
  );
};

export default ActivityTab;
